import { TimeSeriesV2Runtype } from '@luminovo/api-contracts/time-series-schema';
import { z } from 'zod/v4';

/** Canonical monitoring PartCategory path, including descendants; never a catalogue category ID. */
export const CategoryIdSchema = z
    .string()
    .min(1)
    .refine(
        (path) =>
            path ===
            path
                .split(';')
                .map((segment) => segment.trim())
                .filter(Boolean)
                .join(';'),
        'Category paths must contain nonempty, trimmed segments',
    );

/** Browser-safe transport shape; the service validates its configured currency against CurrencyRuntype. */
export const ComponentCurrencySchema = z.string().regex(/^[A-Z]{3}$/);

/** Initial allowlist of component metrics; labels come from their registered definitions. */
export const ComponentMetricIdSchema = z.enum([
    'demand_spend_market_p0',
    'demand_spend_market_p10',
    'demand_spend_market_p50',
    'demand_spend_cheapest_contract_price',
    'demand_spend_latest_contract_price',
    'demand_spend_cheapest_purchase_price',
    'demand_spend_latest_standard_price',
]);
export type ComponentMetricId = z.infer<typeof ComponentMetricIdSchema>;

export const ComponentMetricSelectionSchema = z
    .array(ComponentMetricIdSchema)
    .min(1)
    .max(ComponentMetricIdSchema.options.length)
    .refine((metrics) => new Set(metrics).size === metrics.length, 'Metric IDs must be unique');
/** Only complete values are exposed; absent entries carry no numeric fallback or diagnostics. */
export const ComponentMetricValueSchema = z.object({
    value: z.number().finite().nonnegative(),
});
export type ComponentMetricValue = z.infer<typeof ComponentMetricValueSchema>;

/** Weekly persisted demand snapshots anchored to the selected calculation period. */
export const ComponentDemandSeriesSchema = TimeSeriesV2Runtype.extend({
    intervalDays: z.literal(7),
    values: z.array(z.number().finite().nonnegative().nullable()).length(52),
    issues: z.array(z.number().int()).length(52),
});
export type ComponentDemandSeries = z.infer<typeof ComponentDemandSeriesSchema>;

/** Fixed component columns; only the requested metric keys vary. */
export const ComponentRowSchema = z.object({
    ipn: z.string(),
    description: z.string().nullable(),
    matchedPartCount: z.number().int().nonnegative(),
    // requestedMetrics selects spend columns; demand metrics supplement every stored snapshot read.
    metrics: z.object({
        demand_spend_market_p0: ComponentMetricValueSchema.optional(),
        demand_spend_market_p10: ComponentMetricValueSchema.optional(),
        demand_spend_market_p50: ComponentMetricValueSchema.optional(),
        demand_spend_cheapest_contract_price: ComponentMetricValueSchema.optional(),
        demand_spend_latest_contract_price: ComponentMetricValueSchema.optional(),
        demand_spend_cheapest_purchase_price: ComponentMetricValueSchema.optional(),
        demand_spend_latest_standard_price: ComponentMetricValueSchema.optional(),
        demandQuantity: ComponentMetricValueSchema.optional(),
        demandSeries: ComponentDemandSeriesSchema.optional(),
    }),
});
export type ComponentRow = z.infer<typeof ComponentRowSchema>;

const MetricReferenceSchema = z.object({ kind: z.literal('metric'), metric: ComponentMetricIdSchema });
const DifferenceSchema = z.object({
    kind: z.literal('difference'),
    left: ComponentMetricIdSchema,
    right: ComponentMetricIdSchema,
    positivePart: z.boolean(),
});
/** Deliberately bounded arithmetic, not user-supplied SQL or a recursive formula language. */
export const ComponentValueExpressionSchema = z.discriminatedUnion('kind', [
    MetricReferenceSchema,
    DifferenceSchema,
    z.object({
        kind: z.literal('ratio'),
        numerator: z.discriminatedUnion('kind', [MetricReferenceSchema, DifferenceSchema]),
        denominator: ComponentMetricIdSchema,
    }),
]);
export type ComponentValueExpression = z.infer<typeof ComponentValueExpressionSchema>;

export const ComponentFiltersSchema = z.object({
    search: z.string().trim().max(500).optional(),
    ipns: z.array(z.string().min(1).max(500)).max(1000).optional(),
    values: z
        .array(
            z.object({
                expression: ComponentValueExpressionSchema,
                operator: z.enum(['lt', 'lte', 'eq', 'ne', 'gte', 'gt']),
                value: z.number().finite(),
            }),
        )
        .max(20)
        .default([]),
    text: z
        .array(
            z.union([
                z.object({
                    field: z.literal('ipn'),
                    operator: z.enum(['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith']),
                    value: z.string().min(1).max(500),
                }),
                z.object({
                    field: z.literal('ipn'),
                    operator: z.enum(['blank', 'notBlank']),
                    value: z.string().max(500).optional(),
                }),
            ]),
        )
        .max(10)
        .optional(),
    missing: z
        .array(z.object({ expression: ComponentValueExpressionSchema, missing: z.boolean() }))
        .max(10)
        .optional(),
});
export type ComponentFilters = z.infer<typeof ComponentFiltersSchema>;
const OrderFieldSchema = z.union([
    z.object({ kind: z.literal('column'), field: z.enum(['ipn', 'description', 'matchedPartCount']) }),
    ComponentValueExpressionSchema,
]);
export const ComponentOrderSchema = z.object({
    field: OrderFieldSchema,
    direction: z.enum(['asc', 'desc']),
});
export type ComponentOrder = z.infer<typeof ComponentOrderSchema>;

export const ComponentFindAvailableSnapshotsInputSchema = z.object({ metrics: ComponentMetricSelectionSchema });
export const ComponentListInputSchema = z.object({
    snapshotId: z.string().min(1).max(100),
    categoryId: CategoryIdSchema.optional(),
    metrics: ComponentMetricSelectionSchema,
    filters: ComponentFiltersSchema,
    orderBy: z.array(ComponentOrderSchema).max(10),
    page: z.object({ offset: z.number().int().nonnegative(), limit: z.number().int().min(1).max(500) }),
});
export const ComponentGetInputSchema = z.object({
    categoryId: CategoryIdSchema.optional(),
    snapshotId: z.string().min(1).max(100),
    ipn: z.string().min(1).max(500),
    metrics: ComponentMetricSelectionSchema,
});
export type ComponentFindAvailableSnapshotsInput = z.infer<typeof ComponentFindAvailableSnapshotsInputSchema>;
export type ComponentListInput = z.infer<typeof ComponentListInputSchema>;
export type ComponentGetInput = z.infer<typeof ComponentGetInputSchema>;

/** Completion of a computation only; neither row completeness nor source freshness. */
export const ComponentSavingsCompletionSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('unknown'), lastCompletedAt: z.null() }),
    z.object({ status: z.enum(['fresh', 'stale']), lastCompletedAt: z.iso.datetime() }),
]);
export type ComponentSavingsCompletion = z.infer<typeof ComponentSavingsCompletionSchema>;

/** MIS-665: these are eventually consistent calculation periods, not atomic snapshots. */
export const ComponentSnapshotSchema = z.object({
    id: z.string(),
    effectiveAt: z.iso.datetime(),
    valuationCutoff: z.iso.datetime().nullable(),
    lastUpdatedAt: z.iso.datetime(),
    // Older servers/fixtures have no completion evidence; consumers must treat absence as unknown.
    completion: ComponentSavingsCompletionSchema.nullish(),
    version: z.number().int().positive(),
    currency: ComponentCurrencySchema,
    status: z.enum(['pending', 'fulfilled', 'rejected']),
});
export type ComponentSnapshot = z.infer<typeof ComponentSnapshotSchema>;
export const ComponentFindAvailableSnapshotsOutputSchema = z.object({
    snapshots: z.array(ComponentSnapshotSchema),
    metrics: z.array(
        z.object({
            id: ComponentMetricIdSchema,
            name: z.string(),
            description: z.string(),
            unit: z.literal('currency'),
        }),
    ),
});
export type ComponentFindAvailableSnapshotsOutput = z.infer<typeof ComponentFindAvailableSnapshotsOutputSchema>;

export function snapshotReadResultSchema<T extends z.ZodType>(data: T) {
    return z.discriminatedUnion('status', [
        z.object({
            status: z.literal('pending'),
            snapshotId: z.string(),
            reason: z.enum(['preparing', 'recomputing']),
        }),
        z.object({ status: z.literal('fulfilled'), data }),
        z.object({ status: z.literal('rejected'), snapshotId: z.string(), reason: z.literal('failed') }),
    ]);
}

const ReadMetadataSchema = z.object({
    snapshotId: z.string(),
    requestedMetrics: ComponentMetricSelectionSchema,
    currency: ComponentCurrencySchema,
});
export const ComponentListDataSchema = ReadMetadataSchema.extend({
    components: z.array(ComponentRowSchema),
    pagination: z.object({
        offset: z.number().int().nonnegative(),
        limit: z.number().int().positive(),
        totalRowCount: z.number().int().nonnegative(),
        nextOffset: z.number().int().nonnegative().nullable(),
    }),
});
export const ComponentPartSummarySchema = z.object({
    id: z.string(),
    mpn: z.string().nullable(),
    manufacturer: z.string().nullable(),
    description: z.string().nullable(),
});
export const ComponentGetDataSchema = ReadMetadataSchema.extend({
    component: ComponentRowSchema,
    matchedParts: z.array(ComponentPartSummarySchema),
});
export const ComponentListOutputSchema = snapshotReadResultSchema(ComponentListDataSchema);
export const ComponentGetOutputSchema = snapshotReadResultSchema(ComponentGetDataSchema);
export type ComponentListData = z.infer<typeof ComponentListDataSchema>;
export type ComponentGetData = z.infer<typeof ComponentGetDataSchema>;
export type ComponentListOutput = z.infer<typeof ComponentListOutputSchema>;
export type ComponentGetOutput = z.infer<typeof ComponentGetOutputSchema>;
